import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ClipboardList, Loader2, Plus, Trash2 } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { StoreProfileDetail, StoreSurvey } from '../../types';
import { ProvenanceBadge } from '../ProvenanceBadge';

interface SurveysPanelProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
}

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived: 'bg-slate-100 text-slate-600 border-slate-200',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  in_progress: 'In progress',
  completed: 'Completed',
  archived: 'Archived',
};

/**
 * Dated store surveys — the temporal spine of the twin. "Add survey" posts to
 * the live endpoint and gives immediate visible feedback (row appears in the
 * list + toast); failures surface the server error verbatim.
 */
export function SurveysPanel({ profile, storeId, storeIdParam }: SurveysPanelProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [surveyDate, setSurveyDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [pendingSurveyId, setPendingSurveyId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canEdit = storeId != null && storeIdParam != null;

  const surveys = [...profile.surveys].sort((a, b) => b.surveyDate.localeCompare(a.surveyDate));

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles/summary'] }),
    ]);
  };

  const createSurvey = async () => {
    if (storeId == null || isSaving) return;
    if (!title.trim()) {
      toast({ title: 'A survey title is required', variant: 'destructive' });
      return;
    }
    setIsSaving(true);
    try {
      const res = await apiRequest(`/api/store-profiles/${storeId}/surveys`, 'POST', {
        title: title.trim(),
        surveyDate,
        status: 'in_progress',
        notes: notes.trim() || null,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to create survey');
      }
      const created = (await res.json()) as StoreSurvey;
      await invalidate();
      toast({
        title: 'Survey added',
        description: `"${created.title}" is now on record for ${created.surveyDate}.`,
      });
      setDialogOpen(false);
      setTitle('');
      setNotes('');
    } catch (error) {
      toast({
        title: 'Could not add survey',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateStatus = async (survey: StoreSurvey, status: string) => {
    if (storeId == null) return;
    setPendingSurveyId(survey.id);
    try {
      const res = await apiRequest(`/api/store-profiles/${storeId}/surveys/${survey.id}`, 'PATCH', { status });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to update survey');
      }
      await invalidate();
      toast({ title: `Survey marked ${STATUS_LABELS[status]?.toLowerCase() ?? status}` });
    } catch (error) {
      toast({
        title: 'Could not update survey',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setPendingSurveyId(null);
    }
  };

  const deleteSurvey = async (survey: StoreSurvey) => {
    if (storeId == null) return;
    setPendingSurveyId(survey.id);
    try {
      const res = await apiRequest(`/api/store-profiles/${storeId}/surveys/${survey.id}`, 'DELETE');
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to delete survey');
      }
      await invalidate();
      toast({ title: 'Survey removed' });
    } catch (error) {
      toast({
        title: 'Could not delete survey',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setPendingSurveyId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          <ClipboardList className="h-4 w-4" aria-hidden />
          Surveys
          <ProvenanceBadge provenance={surveys.length > 0 ? 'live' : 'missing'} />
          <Badge variant="secondary">{surveys.length}</Badge>
          {canEdit && (
            <Button type="button" size="sm" className="ml-auto" onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-1" aria-hidden />
              Add survey
            </Button>
          )}
        </CardTitle>
        <p className="text-sm text-gray-500">
          Deliberate dated surveys. The latest completed or in-progress survey feeds "last verified survey" in the header.
        </p>
      </CardHeader>
      <CardContent>
        {surveys.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">
            No surveys on record. Add the first survey to start the store's verification history.
          </p>
        ) : (
          <ul className="space-y-2">
            {surveys.map((survey) => (
              <li key={survey.id} className="border rounded-lg p-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-sm">{survey.title}</p>
                    <Badge variant="outline" className={STATUS_STYLES[survey.status] ?? ''}>
                      {STATUS_LABELS[survey.status] ?? survey.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {survey.surveyDate}
                    {survey.createdByName ? ` · by ${survey.createdByName}` : ''}
                  </p>
                  {survey.notes && <p className="text-xs text-gray-600 mt-1">{survey.notes}</p>}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-2 shrink-0">
                    {survey.status !== 'completed' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={pendingSurveyId === survey.id}
                        onClick={() => void updateStatus(survey, 'completed')}
                      >
                        {pendingSurveyId === survey.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : 'Mark complete'}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete survey ${survey.title}`}
                      disabled={pendingSurveyId === survey.id}
                      onClick={() => void deleteSurvey(survey)}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" aria-hidden />
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add survey</DialogTitle>
            <DialogDescription>
              Record a dated store survey. It appears in the list immediately after saving.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="survey-title">Title</Label>
              <Input
                id="survey-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="e.g. Q3 layout verification"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="survey-date">Survey date</Label>
              <Input
                id="survey-date"
                type="date"
                value={surveyDate}
                onChange={(event) => setSurveyDate(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="survey-notes">Notes (optional)</Label>
              <Textarea
                id="survey-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="What was verified, anything notable…"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button type="button" onClick={() => void createSurvey()} disabled={isSaving}>
                {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />}
                Save survey
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
